import { forwardRef, useImperativeHandle } from 'react'
import { DateRange } from '~/types'
import { WaterStation } from '../schemas'
import { DateTime } from 'luxon'
import { Callout, Flex, Spinner, Strong } from '@radix-ui/themes'
import { AreaChart, BarChart } from '@tremor/react'
import _ from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { GoSquareFill } from 'react-icons/go'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { formatNumber } from '~/utils/formating'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { useMemo } from 'react'
import { useTRPC } from '~/utils/trpc'
import { Resolution } from '@willie/schemas'
import { formatDateLabel } from '~/features/dashboarding/utils'
import { ChartType } from '~/features/dashboarding/schemas'
import { ChartVignettes, ChartVignette } from '~/features/dashboarding/components/ChartVignette'

type MetaOptions = {
  consumption?: boolean
  leak?: boolean
  lostWater?: boolean
  warning?: boolean
}

type LeakChartProps = {
  station: WaterStation
  metaOptions?: MetaOptions | 'all'
  title?: string
  resolution?: Resolution
  chartType?: ChartType
  viewMode?: ViewMode
} & DateRange

export const ViewModes = ['lossPercent', 'partition'] as const
export type ViewMode = (typeof ViewModes)[number]

const StationLeakChart = forwardRef(
  (
    {
      station,
      title,
      resolution = 'day',
      metaOptions = 'all',
      chartType = 'area',
      viewMode = 'partition',
      ...props
    }: LeakChartProps,
    ref
  ) => {
    const { t } = useTranslation(['sites', 'common'])
    const locale = useCurrentLanguage()
    const trpc = useTRPC()

    const { data: leakData = [], isPending: leakdataPending } = useQuery(
      trpc.stations.getStationLeakData.queryOptions({
        stationID: station.id,
        startDate: props.startDate.toISO()!,
        endDate: props.endDate.toISO()!,
        resolution
      })
    )

    const { chartData, consolidatedOverPeriod } = useMemo(() => {
      const chartData = leakData.map(({ dateTime, consumption, leak, lossPercent }, __, all) => {
        return {
          date: formatDateLabel(dateTime, resolution, locale, all.length),
          [t('stations.leakSection.chart.consumption')]: consumption,
          [t('stations.leakSection.chart.leak')]: leak,
          [t('stations.leakSection.chart.lossPercent')]: lossPercent
        }
      })

      const consolidatedConsumption = _.sumBy(leakData, 'consumption')
      const consolidatedLeak = _.sumBy(leakData, 'leak')

      return {
        chartData,
        consolidatedOverPeriod: {
          consumption: consolidatedConsumption,
          leak: consolidatedLeak,
          lossPercent: consolidatedConsumption === 0 ? 0 : (consolidatedLeak / consolidatedConsumption) * 100
        }
      }
    }, [leakData])

    const formatVignetteDate = (date: DateTime) =>
      date.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' }, { locale })

    // Determine if labels should be rotated
    const npoints = chartData.length
    const shouldRotateLabels = () => {
      if (resolution === 'hour' && npoints > 12) return true // Rotate if more than 12 hours
      if (resolution === 'day' && npoints > 15 && npoints <= 30) return true // Rotate for daily if many points
      if (resolution === 'week' && npoints > 8) return true // Rotate if more than 8 weeks
      // Don't rotate for month view unless necessary (handled by Tremor potentially)
      return false
    }

    const shouldRenderMetaKey = (metaKey: keyof MetaOptions) =>
      metaOptions === 'all' || (metaOptions?.[metaKey] ?? false)
    const shouldRenderMeta = metaOptions === 'all' || Object.values(metaOptions).length > 0

    const allChartProps = {
      rotateLabelX: {
        angle: shouldRotateLabels() ? -35 : 0, // Adjust angle based on resolution/data points
        verticalShift: shouldRotateLabels() ? 20 : undefined,
        xAxisHeight: shouldRotateLabels() ? (resolution === 'hour' ? 100 : 60) : undefined
      },
      yAxisLabel: t('stations.leakSection.chart.yAxisLabel'),
      showGridLines: false,
      showGradient: true,
      data: chartData,
      categories:
        viewMode === 'lossPercent'
          ? [t('stations.leakSection.chart.lossPercent')]
          : [t('stations.leakSection.chart.leak'), t('stations.leakSection.chart.consumption')],
      colors: ['red', 'sky'],
      index: 'date',
      showAnimation: true,
      showLegend: false,
      valueFormatter: (value: number) => {
        if (viewMode === 'lossPercent') {
          return `${Math.round(value)} %`
        }
        return `${formatNumber(value)} m³`
      }
    }

    useImperativeHandle(ref, () => ({
      getExportData: () => chartData
    }))

    return (
      <Flex direction={'column'} gap="5">
        {shouldRenderMetaKey('warning') &&
          _.isNumber(consolidatedOverPeriod.lossPercent) &&
          consolidatedOverPeriod.lossPercent >= 10 && (
            <Callout.Root color="amber" className="mb-4 no-export">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Callout.Text>
                {t('stations.leakSection.chart.leakDetected')}{' '}
                <Strong>{Math.round(consolidatedOverPeriod.lossPercent)} %</Strong>{' '}
                {t('stations.leakSection.chart.lossPercentage')} . {t('stations.leakSection.chart.leakFix')}
              </Callout.Text>
            </Callout.Root>
          )}

        {shouldRenderMeta &&
          (leakdataPending ? (
            <ChartVignettes>
              {_.range(4).map((i) => (
                <ChartVignette
                  loading
                  key={i}
                  title="Lorem consumption"
                  subtitle=""
                  value={
                    <>
                      <span className="opacity-50">some m3</span>
                    </>
                  }
                />
              ))}
            </ChartVignettes>
          ) : (
            <ChartVignettes className="px-8 py-2">
              {shouldRenderMetaKey('consumption') && (
                <ChartVignette
                  title={t('stations.leakSection.chart.consumptionHeader')}
                  icon={<GoSquareFill className="fill-sky-400" size={22} />}
                  subtitle={`${formatVignetteDate(props.startDate)} ${t('budget.chart.dateTo')} ${formatVignetteDate(props.endDate)}`}
                  value={
                    <>
                      {formatNumber(consolidatedOverPeriod.consumption)} m<sup>3</sup>
                    </>
                  }
                />
              )}
              {shouldRenderMetaKey('leak') && (
                <ChartVignette
                  title={t('stations.leakSection.chart.leakOverPeriodHeader')}
                  icon={<GoSquareFill className="fill-red-500" size={22} />}
                  subtitle={`${formatVignetteDate(props.startDate)} ${t('budget.chart.dateTo')} ${formatVignetteDate(props.endDate)}`}
                  value={
                    <>
                      {formatNumber(consolidatedOverPeriod.leak)} m<sup>3</sup>
                    </>
                  }
                />
              )}
              {shouldRenderMetaKey('lostWater') && (
                <ChartVignette
                  title={t('stations.leakSection.chart.lostWater')}
                  subtitle={`${formatVignetteDate(props.startDate)} ${t('budget.chart.dateTo')} ${formatVignetteDate(props.endDate)}`}
                  value={
                    consolidatedOverPeriod.lossPercent === 0 ? (
                      '-'
                    ) : (
                      <>{Math.round(consolidatedOverPeriod.lossPercent)} %</>
                    )
                  }
                />
              )}
            </ChartVignettes>
          ))}

        {leakdataPending ? (
          <Flex align={'center'} justify={'center'} className="min-h-32">
            <Spinner />
          </Flex>
        ) : chartType === 'area' ? (
          <AreaChart {...allChartProps} intervalType={'preserveStartEnd'} />
        ) : (
          <BarChart {...allChartProps} intervalType={'preserveStartEnd'} stack />
        )}
      </Flex>
    )
  }
)

export { StationLeakChart }
